import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../workspace/context/AuthContext';

const FavouritesContext = createContext();

const STORAGE_KEY = 'bp_favourite_plans'; // legacy name kept for migration

/**
 * Build the composite key used internally. Format: "kind:slug".
 * `kind` is one of 'plan' | 'land' | 'hl_package'.
 */
const makeKey = (kind, slug) => `${kind}:${slug}`;

/**
 * Migrate legacy storage (plans-only, keyed by full_slug). Anything that
 * looks like an old entry - no `kind`, has `full_slug` - gets re-keyed
 * to `plan:<full_slug>` and tagged kind:'plan'.
 */
const migrateLegacy = (raw) => {
  if (!raw || typeof raw !== 'object') return {};
  const out = {};
  for (const [k, v] of Object.entries(raw)) {
    if (!v || typeof v !== 'object') continue;
    if (v.kind) {
      // Already migrated.
      out[k] = v;
      continue;
    }
    if (v.full_slug) {
      const key = makeKey('plan', v.full_slug);
      out[key] = { ...v, kind: 'plan', __key: key };
    }
  }
  return out;
};

/** Pull the per-item slug used for the composite key. */
const slugFor = (item, kind) => {
  if (kind === 'plan') return item.full_slug || `${item.builder_slug}/${item.plan_slug}`;
  if (kind === 'land') return item.listing_slug || item.slug || item.id;
  if (kind === 'hl_package') return item.listing_slug || item.slug || item.id;
  if (kind === 'builder') return item.builder_slug || item.slug || String(item.id || item.business_name || '').toLowerCase().replace(/\s+/g, '-');
  if (kind === 'article') return item.slug || (item.path || '').replace(/^\/+/, '').replace(/\//g, '-');
  if (kind === 'suburb') return item.suburb_slug || item.slug;
  if (kind === 'product') return String(item.id || item.sku || item.slug || '');
  if (kind === 'display_home') return String(item.id || item.slug || `${item.builder_slug || ''}-${item.suburb_slug || item.suburb || ''}`);
  return item.id || item.slug;
};

/** Normalise the saved record per kind so the sidebar can render without
 *  pulling a fresh API call. */
const normalise = (item, kind) => {
  if (kind === 'plan') {
    return {
      kind: 'plan',
      full_slug: item.full_slug,
      plan_name: item.plan_name,
      builder_name: item.builder_name,
      builder_slug: item.builder_slug,
      plan_slug: item.plan_slug,
      bedrooms: item.bedrooms,
      bathrooms: item.bathrooms,
      total_sqm: item.total_sqm,
      stories: item.stories,
      garage: item.garage,
      facade: item.facade,
    };
  }
  if (kind === 'land') {
    return {
      kind: 'land',
      id: item.id,
      suburb: item.suburb,
      suburb_slug: item.suburb_slug,
      listing_slug: item.listing_slug || item.slug,
      area_m2: item.area_m2,
      frontage_m: item.frontage_m,
      depth_m: item.depth_m,
      estate_name: item.estate_name,
      // Full address fields - populated when the listing came from an
      // auth-gated endpoint. Optional.
      lot_number:     item.lot_number || item.lot,
      street_address: item.street_address || item.address || item.street,
      state:          item.state,
      postcode:       item.postcode,
      hero_image: (item.marketing_images && item.marketing_images[0]) || (item.images && item.images[0]) || null,
      detail_path: item.detail_path || `/land-for-sale-adelaide/${item.suburb_slug}/${item.listing_slug || item.slug}`,
    };
  }
  if (kind === 'hl_package') {
    return {
      kind: 'hl_package',
      id: item.id,
      package_name: item.package_name || item.listing_name || item.name,
      builder_name: item.builder_name,
      suburb: item.suburb,
      suburb_slug: item.suburb_slug,
      listing_slug: item.listing_slug || item.slug,
      bedrooms: item.bedrooms,
      bathrooms: item.bathrooms,
      garage: item.garage,
      total_sqm: item.total_sqm,
      is_double_storey: item.is_double_storey,
      // Full address fields - populated when the listing payload came
      // from the auth-gated /api/packages/full endpoint. Optional;
      // sidebar falls back to the suburb-only label when missing.
      lot_number:     item.lot_number || item.lot,
      street_address: item.street_address || item.address || item.street,
      state:          item.state,
      postcode:       item.postcode,
      estate_name:    item.estate_name,
      hero_image: (item.marketing_images && item.marketing_images[0]) || (item.images && item.images[0]) || null,
      detail_path: item.detail_path || `/house-and-land-adelaide/${item.suburb_slug}/${item.listing_slug || item.slug}`,
    };
  }
  if (kind === 'builder') {
    // 2026-feb: Shortlisted builders from the Find a Builder tool.
    // Normalised so the sidebar can render the saved record without
    // re-fetching the matchmaker (which would require re-doing the
    // 11-question brief).
    return {
      kind: 'builder',
      id: item.id,
      business_name: item.business_name || item.name,
      builder_slug: item.builder_slug || item.slug,
      state: item.state,
      regions_display: item.regions_display,
      logo_url: item.logo_url || item.builder_logo_url,
      website: item.website,
      builder_size: item.builder_size,
      // Capture the user's brief state at save time so the sidebar
      // can deep-link back into the relevant Find-a-Builder context.
      saved_for_state: item.saved_for_state || item.state,
      match_score: item.match_score,
      match_reason: item.match_reason,
    };
  }
  if (kind === 'article') {
    // 2026-02: Long-form guides, blog posts and pillar pages users can
    // bookmark from anywhere on the public site. We store the minimum
    // needed to render a sidebar row + send the user back to the page.
    return {
      kind: 'article',
      slug: item.slug || (item.path || '').replace(/^\/+/, '').replace(/\//g, '-'),
      title: item.title,
      summary: item.summary || item.excerpt || '',
      detail_path: item.detail_path || item.path || `/${item.slug || ''}`,
      hero_image: item.hero_image || item.image || null,
    };
  }
  if (kind === 'suburb') {
    // 2026-02: Suburb bookmarks - lets users compile a personal
    // suburb shortlist as they research locations.
    return {
      kind: 'suburb',
      suburb_slug: item.suburb_slug || item.slug,
      name: item.name || item.suburb,
      state: item.state || 'SA',
      postcode: item.postcode || null,
      detail_path: item.detail_path || `/home-builders-in/${item.suburb_slug || item.slug}`,
      hero_image: item.hero_image || null,
    };
  }
  if (kind === 'product') {
    // 2026-06: "Save to my build" - affiliate products users want to
    // revisit as they fit out/renovate. We store the tracked go_url so
    // the sidebar can send them straight to the partner, plus the
    // minimum needed to render the row offline (no re-fetch).
    return {
      kind: 'product',
      id: String(item.id || item.sku || item.slug || ''),
      name: item.name,
      image: item.image || null,
      price: item.price ?? null,
      list_price: item.list_price ?? null,
      save: item.save ?? 0,
      go_url: item.go_url || null,
      supplier: item.supplier ? { key: item.supplier.key, name: item.supplier.name } : null,
      product_kind: item.kind || '',
    };
  }
  if (kind === 'display_home') {
    return normaliseDisplayHome(item);
  }
  return { ...item, kind };
};

const normaliseDisplayHome = (item) => ({
  kind: 'display_home',
  id: String(item.id || item.slug || `${item.builder_slug || ''}-${item.suburb_slug || item.suburb || ''}`),
  name: item.name || item.title || item.display_name || (item.suburb ? `Display home in ${item.suburb}` : 'Display home'),
  builder_name: item.builder_name || item.builder || null,
  suburb: item.suburb || null,
  state: item.state || null,
  address: item.address || null,
  hero_image: item.hero_image || item.image || (Array.isArray(item.images) && item.images[0]) || null,
  website: item.website || item.url || null,
});

export const FavouritesProvider = ({ children }) => {
  const { session, user } = useAuth() || {};
  const userId = user?.id || null;
  const accessToken = session?.access_token || null;
  const prevUserIdRef = useRef(null);
  const [favourites, setFavourites] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      const parsed = stored ? JSON.parse(stored) : {};
      return migrateLegacy(parsed);
    } catch { return {}; }
  });
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Which tab is active inside the sidebar drawer. Lifted here so the
  // tab-open + which-tab can be set from outside (e.g. on Land hub:
  // "Open Shortlist on the Land tab").
  const [sidebarTab, setSidebarTab] = useState('plan');

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(favourites));
  }, [favourites]);

  // ─── Per-user server sync ───────────────────────────────────────
  // When the user logs IN: pull their saved shortlist from the server,
  // merge any anonymous localStorage items into it, push the union back.
  // When the user logs OUT: clear the in-memory map so the next visitor
  // on this browser starts fresh.
  useEffect(() => {
    const API = process.env.REACT_APP_BACKEND_URL;
    if (!API) return undefined;

    // Logout → clear
    if (!userId && prevUserIdRef.current) {
      prevUserIdRef.current = null;
      setFavourites({});
      return undefined;
    }

    if (!userId || !accessToken) {
      prevUserIdRef.current = userId;
      return undefined;
    }

    // Only run on login transition (or first mount with a session)
    if (prevUserIdRef.current === userId) return undefined;
    prevUserIdRef.current = userId;

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/api/shortlist`, {
          headers: { 'Authorization': `Bearer ${accessToken}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        const serverItems = data?.items || {};
        if (cancelled) return;
        setFavourites((local) => {
          // Merge: server is source of truth; local-only entries (added
          // while signed-out) are folded in so the user doesn't lose
          // anything from the pre-login session.
          const merged = { ...local, ...serverItems };
          // Best-effort push the merged map back so server stays in
          // sync with whatever we just folded in.
          fetch(`${API}/api/shortlist`, {
            method:  'PUT',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${accessToken}` },
            body:    JSON.stringify({ items: merged }),
          }).catch(() => {});
          return merged;
        });
      } catch {
        // Silent - keep the local list intact on transient API failure.
      }
    })();
    return () => { cancelled = true; };
  }, [userId, accessToken]);

  // Debounced PUT - every time favourites change for a logged-in user,
  // replace the server-side copy after a 600ms quiet window.
  useEffect(() => {
    const API = process.env.REACT_APP_BACKEND_URL;
    if (!API || !userId || !accessToken) return undefined;
    const t = setTimeout(() => {
      fetch(`${API}/api/shortlist`, {
        method:  'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${accessToken}` },
        body:    JSON.stringify({ items: favourites }),
      }).catch(() => {});
    }, 600);
    return () => clearTimeout(t);
  }, [favourites, userId, accessToken]);

  // ─── Silent address enrichment for logged-in users ──────────────
  // Items shortlisted from the public catalog cards (`/api/packages`,
  // `/api/land`) come in sanitized - no street address / lot number.
  // Once the user logs in we can call the auth-gated detail endpoints
  // and fold the real address into each saved record so the sidebar
  // can show "Lot 73 Hornet Way Virginia, SA 5120" instead of
  // "3 bed in Virginia".
  useEffect(() => {
    const API = process.env.REACT_APP_BACKEND_URL;
    if (!API || !accessToken) return undefined;
    const items = Object.values(favourites);
    const needLand = items.filter(v => v.kind === 'land'       && !v.street_address && (v.id || v.listing_slug));
    const needPkg  = items.filter(v => v.kind === 'hl_package' && !v.street_address && (v.id || v.listing_slug));
    if (!needLand.length && !needPkg.length) return undefined;

    let cancelled = false;
    const auth = { 'Authorization': `Bearer ${accessToken}` };
    (async () => {
      const updates = {};
      const tryFetch = async (url) => {
        try {
          const r = await fetch(url, { headers: auth });
          if (!r.ok) return null;
          const j = await r.json();
          return j?.listing || j || null;
        } catch { return null; }
      };
      for (const it of needLand) {
        const li = await tryFetch(`${API}/api/land/${it.id || it.listing_slug}`);
        if (li) updates[it.__key] = { ...it, ...normalise({ ...it, ...li }, 'land'), __key: it.__key };
      }
      for (const it of needPkg) {
        // Try the auth-gated `/full/{id}` first so we get the real
        // street address; fall back to the public `/{id}` shape.
        let li = await tryFetch(`${API}/api/packages/full/${it.id || it.listing_slug}`);
        if (!li) li = await tryFetch(`${API}/api/packages/${it.id || it.listing_slug}`);
        if (li) updates[it.__key] = { ...it, ...normalise({ ...it, ...li }, 'hl_package'), __key: it.__key };
      }
      if (cancelled || !Object.keys(updates).length) return;
      setFavourites(prev => ({ ...prev, ...updates }));
    })();
    return () => { cancelled = true; };
  }, [accessToken, favourites]);

  // ─── Auto-prune stale shortlist items ───────────────────────────
  // On mount, ask the backend which of our saved plans / land / H&L
  // packages still exist + are Available. Anything the backend says
  // is gone (404) or OnHold / Sold / Withdrawn is removed silently
  // so the sidebar never shows broken links.
  useEffect(() => {
    let cancelled = false;
    const items = Object.values(favourites);
    if (items.length === 0) return undefined;

    const planItems = items.filter((v) => v.kind === 'plan');
    const landItems = items.filter((v) => v.kind === 'land');
    const hlItems   = items.filter((v) => v.kind === 'hl_package');

    const payload = {
      plans:    planItems
        .filter((p) => p.builder_slug && p.plan_slug)
        .map((p) => ({ builder_slug: p.builder_slug, plan_slug: p.plan_slug })),
      land:     landItems.map((p) => p.id || p.listing_slug).filter(Boolean),
      packages: hlItems.map((p) => p.id || p.listing_slug).filter(Boolean),
    };
    if (!payload.plans.length && !payload.land.length && !payload.packages.length) return undefined;

    const API = process.env.REACT_APP_BACKEND_URL;
    if (!API) return undefined;

    (async () => {
      try {
        const res = await fetch(`${API}/api/shortlist/validate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        setFavourites((prev) => {
          const next = { ...prev };
          let removed = 0;
          for (const [k, v] of Object.entries(prev)) {
            let stillAvail = true;
            if (v.kind === 'plan') {
              const full = `${v.builder_slug}/${v.plan_slug}`;
              stillAvail = data?.plans?.[full] !== false;
            } else if (v.kind === 'land') {
              const ref = v.id || v.listing_slug;
              stillAvail = data?.land?.[ref] !== false;
            } else if (v.kind === 'hl_package') {
              const ref = v.id || v.listing_slug;
              stillAvail = data?.packages?.[ref] !== false;
            }
            if (!stillAvail) {
              delete next[k];
              removed += 1;
            }
          }
          return removed > 0 ? next : prev;
        });
      } catch {
        // Silent - a transient API hiccup must NOT wipe the user's list.
      }
    })();

    return () => { cancelled = true; };
    // Run ONCE per session (mount). Re-running on every favourites
    // mutation would race the user's heart-toggle clicks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Add or remove an item. Pass the item + its kind explicitly so we
   * never guess wrong (a land listing and a plan can share an `id`).
   */
  const toggleFavourite = useCallback((item, kind = 'plan') => {
    if (!item) return;
    const slug = slugFor(item, kind);
    if (!slug) return;
    const key = makeKey(kind, slug);
    setFavourites(prev => {
      if (prev[key]) {
        const next = { ...prev };
        delete next[key];
        return next;
      }
      return { ...prev, [key]: { ...normalise(item, kind), __key: key } };
    });
  }, []);

  const isFavourite = useCallback(
    (slugOrItem, kind = 'plan') => {
      const slug = typeof slugOrItem === 'string' ? slugOrItem : slugFor(slugOrItem || {}, kind);
      return !!favourites[makeKey(kind, slug)];
    },
    [favourites],
  );

  const clearAll      = useCallback(() => setFavourites({}), []);
  const clearKind     = useCallback((kind) => setFavourites(prev => Object.fromEntries(Object.entries(prev).filter(([, v]) => v.kind !== kind))), []);
  const openSidebarOn = useCallback((kind) => { setSidebarTab(kind); setSidebarOpen(true); }, []);

  // Derived per-kind lists + counts (memoised so re-renders cheap).
  const { plansList, landList, hlList, buildersList, articlesList, suburbsList, productsList, displayHomesList, count, countByKind, list } = useMemo(() => {
    const all = Object.values(favourites);
    const plans    = all.filter((v) => v.kind === 'plan');
    const land     = all.filter((v) => v.kind === 'land');
    const hl       = all.filter((v) => v.kind === 'hl_package');
    const builders = all.filter((v) => v.kind === 'builder');
    const articles = all.filter((v) => v.kind === 'article');
    const suburbs  = all.filter((v) => v.kind === 'suburb');
    const products = all.filter((v) => v.kind === 'product');
    const displayHomes = all.filter((v) => v.kind === 'display_home');
    return {
      plansList: plans,
      landList:  land,
      hlList:    hl,
      buildersList: builders,
      articlesList: articles,
      suburbsList:  suburbs,
      productsList: products,
      displayHomesList: displayHomes,
      count:     all.length,
      countByKind: { plan: plans.length, land: land.length, hl_package: hl.length, builder: builders.length, article: articles.length, suburb: suburbs.length, product: products.length, display_home: displayHomes.length },
      list:      plans, // legacy alias - pre-multi-kind callers expected plans only
    };
  }, [favourites]);

  return (
    <FavouritesContext.Provider value={{
      favourites,
      // Lists
      list,           // legacy (plans only)
      plansList, landList, hlList, buildersList, articlesList, suburbsList, productsList, displayHomesList,
      // Counts
      count, countByKind,
      // Actions
      toggleFavourite, isFavourite, clearAll, clearKind,
      // Sidebar control
      sidebarOpen, setSidebarOpen,
      sidebarTab, setSidebarTab, openSidebarOn,
    }}>
      {children}
    </FavouritesContext.Provider>
  );
};

export const useFavourites = () => useContext(FavouritesContext);
